import {
  Alert,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import FileDownload from "@mui/icons-material/FileDownload";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { getCIDS } from "../services/cidService";
import { exportCidService } from "../services/importService";
import { notifyService } from "../services/notifyService";

// Saves an axios blob response as a file, honoring the server's filename.
const downloadBlobResponse = (res, fallbackName) => {
  const blob = new Blob([res.data], { type: "text/csv;charset=utf-8;" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  const match = /filename="([^"]+)"/.exec(
    res.headers?.["content-disposition"] || ""
  );
  link.href = url;
  link.download = match ? match[1] : fallbackName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
  return link.download;
};

const ReportsPage = () => {
  const assessorCid = useSelector((state) => state.assessor.value.form.cid);
  const [cids, setCids] = useState([]);
  const [cid, setCid] = useState("");
  const [exporting, setExporting] = useState(false);
  const [lastExport, setLastExport] = useState(null);

  useEffect(() => {
    getCIDS().then((list) => {
      if (list) {
        setCids(list);
      }
    });
  }, []);

  // Default to the CID that's open in the Quick Assessor.
  useEffect(() => {
    if (!cid && assessorCid && cids.some((c) => c.id === assessorCid)) {
      setCid(assessorCid);
    }
  }, [assessorCid, cids, cid]);

  const selectedCid = useMemo(
    () => cids.find((c) => c.id === cid) || null,
    [cids, cid]
  );

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await exportCidService(cid);
      const fileName = downloadBlobResponse(
        res,
        `${selectedCid?.name || `cid-${cid}`}-export.csv`
      );
      setLastExport({ fileName, cidName: selectedCid?.name });
      notifyService("Export downloaded");
    } catch (err) {
      notifyService("Export failed");
      console.log(err);
    } finally {
      setExporting(false);
    }
  };

  return (
    <Grid container sx={{ p: { xs: 1, md: 2 } }} spacing={2}>
      <Grid item xs={12}>
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={0.5}>
              <Typography variant="h5">Reports</Typography>
              <Typography variant="body2" color="text.secondary">
                Pull data out of the CRM whenever you need it.
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={2}>
              <Typography variant="h6">Full CID Export</Typography>
              <Typography variant="body2" color="text.secondary">
                Downloads every property in the CID as a CSV — owner and
                assessment data, marker color, primary and additional contacts,
                and all custom fields. The columns match the import template,
                so an exported file can be edited and re-imported.
              </Typography>

              <FormControl fullWidth size="small">
                <InputLabel>CID</InputLabel>
                <Select
                  label="CID"
                  value={cids.some((c) => c.id === cid) ? cid : ""}
                  onChange={(e) => setCid(e.target.value)}
                >
                  {cids.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Stack direction="row" spacing={2} alignItems="center">
                <Button
                  variant="contained"
                  startIcon={<FileDownload />}
                  disabled={!cid || exporting}
                  onClick={handleExport}
                >
                  Export to CSV
                </Button>
                {exporting ? <CircularProgress size={20} /> : null}
              </Stack>

              {lastExport ? (
                <Alert severity="success">
                  Downloaded {lastExport.fileName}
                </Alert>
              ) : null}
            </Stack>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default ReportsPage;
